import { useEffect, useLayoutEffect } from 'react';

const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const defaultRestoreState = {
  position: '',
  top: '',
  width: '',
  overflow: '',
  overflowY: '',
  touchAction: '',
  overscrollBehavior: '',
  scrollBehavior: ''
};

let lockCount = 0;
let restoreState = { ...defaultRestoreState, scrollY: 0 };

const applyScrollLock = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const body = document.body;
  const html = document.documentElement;

  if (!body || !html) {
    return;
  }

  // Capture styles only on the first lock
  if (lockCount === 0) {
    const bodyStyle = body.style;
    restoreState = {
      position: bodyStyle.position || '',
      top: bodyStyle.top || '',
      width: bodyStyle.width || '',
      overflow: bodyStyle.overflow || '',
      overflowY: bodyStyle.overflowY || '',
      touchAction: bodyStyle.touchAction || '',
      overscrollBehavior: bodyStyle.overscrollBehavior || '',
      scrollBehavior: bodyStyle.scrollBehavior || '',
      scrollY: typeof window !== 'undefined'
        ? window.scrollY || window.pageYOffset || 0
        : 0
    };
  }

  lockCount += 1;

  body.classList.add('loopino-desktop-modal-open');
  html.classList.add('loopino-desktop-modal-open');

  body.style.position = 'fixed';
  body.style.top = `-${restoreState.scrollY}px`;
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  body.style.overflowY = 'hidden';
  body.style.touchAction = 'none';
  body.style.overscrollBehavior = 'contain';
};

const releaseScrollLock = () => {
  if (typeof document === 'undefined') {
    return;
  }

  if (lockCount === 0) {
    return;
  }

  lockCount -= 1;

  if (lockCount > 0) {
    return;
  }

  const body = document.body;
  const html = document.documentElement;

  if (!body || !html) {
    return;
  }

  const bodyStyle = body.style;

  body.classList.remove('loopino-desktop-modal-open');
  html.classList.remove('loopino-desktop-modal-open');

  bodyStyle.position = restoreState.position;
  bodyStyle.top = restoreState.top;
  bodyStyle.width = restoreState.width;
  bodyStyle.overflow = restoreState.overflow;
  bodyStyle.overflowY = restoreState.overflowY;
  bodyStyle.touchAction = restoreState.touchAction;
  bodyStyle.overscrollBehavior = restoreState.overscrollBehavior;
  bodyStyle.scrollBehavior = restoreState.scrollBehavior;

  if (typeof window !== 'undefined') {
    window.scrollTo(0, restoreState.scrollY || 0);
  }

  restoreState = { ...defaultRestoreState, scrollY: 0 };
};

export const forceUnlockScroll = () => {
  if (typeof document === 'undefined') {
    return;
  }

  if (lockCount === 0) {
    lockCount = 1;
  }

  let safety = 16;
  while (lockCount > 0 && safety > 0) {
    releaseScrollLock();
    safety -= 1;
  }

  lockCount = 0;
};

export const useDesktopScrollLock = (isActive, reason = 'default') => {
  useIsomorphicLayoutEffect(() => {
    if (!isActive) {
      return undefined;
    }

    applyScrollLock();

    return () => {
      releaseScrollLock();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, reason]);
};

export default useDesktopScrollLock;
